import { format, parseISO } from 'date-fns';
import { Fixture, GroupedFixtures, StatusVariant } from '@/types/fixtures';

/**
 * Groups fixtures by their reported date
 */
export function groupFixturesByDate(fixtures: Fixture[]): GroupedFixtures {
  return fixtures.reduce((groups: GroupedFixtures, fixture) => {
    const date = parseISO(fixture.reportedTime);
    const dateKey = format(date, 'yyyy-MM-dd');

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }

    groups[dateKey].push(fixture);
    return groups;
  }, {});
}

/**
 * Formats a date string for display in group headers
 */
export function formatFixtureDate(dateString: string): string {
  const date = parseISO(dateString);

  return format(date, 'EEEE, dd MMM').toUpperCase();
}

/**
 * Formats a date with custom pattern
 */
export function formatDate(dateString: string, pattern: string = 'MMM dd, yyyy'): string {
  return format(parseISO(dateString), pattern);
}

/**
 * Formats laycan date range matching Angular logic:
 * - If same date: "25 Feb"
 * - If same month: "25-28 Feb"
 * - If different months: "25 Feb - 28 Mar"
 */
export function formatLayCan(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '';

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  const startFormatted = format(start, 'dd MMM');
  const endFormatted = format(end, 'dd MMM');

  // Different months: "25 Feb - 28 Mar"
  if (start.getMonth() !== end.getMonth()) {
    return `${startFormatted} - ${endFormatted}`;
  }

  // Same date: "25 Feb"
  if (startFormatted === endFormatted) {
    return startFormatted;
  }

  // Same month, different days: "25-28 Feb"
  return `${format(start, 'dd')}-${endFormatted}`;
}

/**
 * Formats cargo size for display
 */
export function formatCargoSize(size: number): string {
  if (!size) return '--';
  return `${Math.round(size).toLocaleString('en-US')} mts`;
}

/**
 * Formats currency values
 */
export function formatCurrency(value: number | null, currency: string = 'USD'): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Formats rate display matching Angular formatting
 * WS: 2 decimal places (WS 69.00)
 * Lumpsum: 3 decimal places in millions (USD 5.250M)
 */
export function formatRate(rate: number | null, rateType: string): string {
  if (rate === null || rate === undefined) {
    return 'N/A';
  }

  const type = rateType.toLowerCase();

  if (type === 'lumpsum') {
    // Format as millions with 3 decimal places
    return `USD ${(rate / 1000000).toFixed(3)}M`;
  }

  if (type === 'ws') {
    // Format with 2 decimal places
    return `WS ${rate.toFixed(2)}`;
  }

  // Default formatting
  return `${rateType} ${rate.toFixed(2)}`;
}

/**
 * Gets badge variant based on fixture status
 */
export function getStatusVariant(status: string): StatusVariant {
  switch (status.toLowerCase()) {
    case 'fixed':
      return 'success';
    case 'subs':
    case 'on subs':
      return 'primary';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Gets status color for additional styling
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'fixed':
      return 'text-green-600 dark:text-green-400';
    case 'subs':
    case 'on subs':
      return 'text-blue-600 dark:text-blue-400';
    case 'failed':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-500 dark:text-gray-400';
  }
}

/**
 * Formats time duration
 */
export function formatDuration(days: number): string {
  if (days < 1) {
    const hours = Math.round(days * 24);
    return `${hours}h`;
  }

  if (days === 1) {
    return '1 day';
  }

  return `${days.toFixed(1)} days`;
}

/**
 * Calculates days between two dates
 */
export function daysBetween(startDate: string, endDate: string): number {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Formats percentage values
 */
export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Sorts fixtures by reported time (newest first)
 */
export function sortFixturesByDate(fixtures: Fixture[]): Fixture[] {
  return [...fixtures].sort(
    (a, b) => new Date(b.reportedTime).getTime() - new Date(a.reportedTime).getTime()
  );
}

/**
 * Filters fixtures based on search criteria
 */
export function filterFixtures(fixtures: Fixture[], searchTerm: string = ''): Fixture[] {
  if (!searchTerm.trim()) {
    return fixtures;
  }

  const term = searchTerm.toLowerCase();

  return fixtures.filter(
    (fixture) =>
      fixture.vesselName.toLowerCase().includes(term) ||
      fixture.charterer.toLowerCase().includes(term) ||
      fixture.trade.toLowerCase().includes(term) ||
      fixture.operator.toLowerCase().includes(term) ||
      fixture.status.toLowerCase().includes(term)
  );
}

/**
 * Gets fixture summary statistics
 */
export function getFixtureStats(fixtures: Fixture[]) {
  const totalFixtures = fixtures.length;
  const statusCounts = fixtures.reduce(
    (acc, fixture) => {
      acc[fixture.status] = (acc[fixture.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const totalCargo = fixtures.reduce((sum, fixture) => sum + fixture.cargoSize, 0);
  const avgCargoSize = totalFixtures > 0 ? totalCargo / totalFixtures : 0;

  return {
    totalFixtures,
    statusCounts,
    totalCargo,
    avgCargoSize: Math.round(avgCargoSize),
  };
}

/**
 * Validates fixture data
 */
export function isValidFixture(fixture: unknown): fixture is Fixture {
  return Boolean(
    fixture &&
      typeof fixture === 'object' &&
      typeof (fixture as Fixture).fixtureId === 'number' &&
      typeof (fixture as Fixture).vesselName === 'string' &&
      typeof (fixture as Fixture).reportedTime === 'string' &&
      typeof (fixture as Fixture).status === 'string'
  );
}

/**
 * Resolves the display label + Badge variant for a Recent Fixture vessel size.
 * TIXFixtures encoding: 2 = VLCC, 3 = Suezmax. Returns null for unknown/missing
 * sizes so callers can render nothing.
 *
 * NOTE: this is intentionally separate from positionListUtils.getVesselSizeInfo,
 * which uses a different encoding (1 = Suezmax there vs 3 here).
 */
export function getFixtureVesselSizeInfo(
  vesselSizeId: number | null | undefined
): { label: string; variant: 'primary' | 'success' } | null {
  switch (vesselSizeId) {
    case 2:
      return { label: 'VLCC', variant: 'primary' };
    case 3:
      return { label: 'Suezmax', variant: 'success' };
    default:
      return null;
  }
}
